<template>
  <div class="food-category-manage">
    <a-config-provider :locale="zhCN">
      <a-card :bordered="false">
        <template #title>
          <div style="display: flex; align-items: center; gap: 12px">
            <a-button type="text" style="padding: 0" @click="goBack">
              <template #icon><ArrowLeftOutlined /></template>
            </a-button>
            <span style="font-size: 18px; font-weight: 600">菜品分类管理</span>
          </div>
        </template>

        <!-- 查询表单 -->
        <a-form
          :model="queryParams"
          layout="inline"
          class="search-form"
          @submit="handleSearch"
          @submit.prevent
        >
          <a-form-item label="店铺">
            <a-select
              v-model:value="queryParams.data.shopId"
              disabled
              placeholder="请选择店铺"
              style="width: 200px"
              @change="cacheSelectedShop"
            >
              <a-select-option v-for="shop in shopList" :key="shop.shopId" :value="shop.shopId">
                {{ shop.shopName }}
              </a-select-option>
            </a-select>
          </a-form-item>

          <a-form-item label="分类名称">
            <a-input
              v-model:value="queryParams.data.categoryName"
              placeholder="请输入分类名称"
              allow-clear
              style="width: 200px"
            />
          </a-form-item>

          <a-form-item label="状态">
            <a-select
              v-model:value="queryParams.data.categoryStatus"
              placeholder="请选择状态"
              allow-clear
              style="width: 120px"
            >
              <a-select-option :value="0">正常</a-select-option>
              <a-select-option :value="1">禁用</a-select-option>
            </a-select>
          </a-form-item>

          <a-form-item label="创建时间">
            <a-range-picker
              v-model:value="dateRange"
              format="YYYY-MM-DD"
              style="width: 240px"
              @change="handleDateRangeChange"
            />
          </a-form-item>

          <a-form-item>
            <a-button type="primary" html-type="submit" :loading="loading">
              <template #icon><SearchOutlined /></template>
              查询
            </a-button>
            <a-button style="margin-left: 8px" @click="handleReset">
              <template #icon><ReloadOutlined /></template>
              重置
            </a-button>
          </a-form-item>
        </a-form>

        <!-- 顶部操作 -->
        <div style="margin-bottom: 16px; text-align: right">
          <a-button type="primary" @click="openAddCategoryModal"> 新增分类 </a-button>
        </div>

        <!-- 数据表格 -->
        <a-table
          :columns="columns"
          :data-source="tableData"
          :loading="loading"
          :pagination="false"
          row-key="shopFoodCategoryId"
          :scroll="{ x: 'max-content' }"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'categoryStatus'">
              <a-tag :color="record.categoryStatus === 0 ? 'green' : 'red'">
                {{ record.categoryStatus === 0 ? '正常' : '禁用' }}
              </a-tag>
            </template>
            <template v-else-if="column.key === 'shopName'">
              <span>{{ getShopName(record.shopId) }}</span>
            </template>
            <template v-else-if="column.key === 'createTime'">
              <span>{{ formatDateTime(record.createTime) }}</span>
            </template>
            <template v-else-if="column.key === 'actions'">
              <a-space>
                <a-button type="link" size="small" @click="handleEditCategory(record)">
                  编辑
                </a-button>
                <a-popconfirm
                  :title="record.categoryStatus === 0 ? '确认禁用该分类？' : '确认启用该分类？'"
                  ok-text="是"
                  cancel-text="否"
                  @confirm="handleToggleCategoryStatus(record)"
                >
                  <a-button type="link" size="small">
                    {{ record.categoryStatus === 0 ? '禁用' : '启用' }}
                  </a-button>
                </a-popconfirm>
                <a-popconfirm
                  title="确认删除该分类？"
                  ok-text="是"
                  cancel-text="否"
                  @confirm="handleDeleteCategory(record)"
                >
                  <a-button type="link" danger size="small">删除</a-button>
                </a-popconfirm>
              </a-space>
            </template>
          </template>
        </a-table>

        <!-- 分页 -->
        <div class="pagination-container">
          <a-pagination
            v-model:current="queryParams.pageNum"
            v-model:page-size="queryParams.pageSize"
            :total="total"
            :show-total="(total) => `共 ${total} 条`"
            :show-size-changer="true"
            :page-size-options="['10', '20', '50', '100']"
            @change="handlePageChange"
            @show-size-change="handlePageSizeChange"
          />
        </div>
      </a-card>
    </a-config-provider>
  </div>

  <!-- 新增 / 编辑分类弹窗 -->
  <a-modal
    v-model:open="categoryModalVisible"
    :title="categoryModalTitle"
    :confirm-loading="categoryModalLoading"
    ok-text="保存"
    cancel-text="取消"
    :width="640"
    wrap-class-name="category-modal"
    @ok="handleCategoryModalOk"
    @cancel="handleCategoryModalCancel"
  >
    <a-form layout="vertical">
      <a-form-item label="店铺" required>
        <!-- 新增时选择店铺，编辑时仅展示店铺名称 -->
        <a-select
          v-if="!isEdit"
          v-model:value="categoryForm.shopId"
          disabled
          placeholder="请选择店铺"
        >
          <a-select-option v-for="shop in shopList" :key="shop.shopId" :value="shop.shopId">
            {{ shop.shopName }}
          </a-select-option>
        </a-select>
        <a-input v-else :value="getShopName(categoryForm.shopId)" disabled placeholder="店铺" />
      </a-form-item>
      <a-form-item label="分类名称" required>
        <a-input v-model:value="categoryForm.categoryName" placeholder="请输入分类名称" />
      </a-form-item>
      <a-form-item label="翻译语言">
        <a-select
          v-model:value="categoryForm.translationLanguage"
          placeholder="请选择翻译语言"
          @change="handleCategoryTranslationLanguageChange"
        >
          <a-select-option
            v-for="language in translationLanguageOptions"
            :key="language.value"
            :value="language.value"
          >
            {{ language.label }}
          </a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item label="分类翻译名称">
        <a-input
          v-model:value="categoryForm.translationCategoryName"
          placeholder="请输入分类翻译名称"
        />
      </a-form-item>
      <a-form-item label="排序">
        <a-input-number
          v-model:value="categoryForm.sort"
          :min="0"
          style="width: 100%"
          placeholder="请输入排序（数字，越小越靠前）"
        />
      </a-form-item>
      <a-form-item label="备注">
        <a-textarea
          v-model:value="categoryForm.remark"
          :rows="3"
          :maxlength="200"
          show-count
          placeholder="请输入备注"
        />
      </a-form-item>
    </a-form>
  </a-modal>

  <!-- 分类详情弹窗 -->
  <a-modal v-model:open="categoryDetailVisible" title="分类详情" :footer="null" :width="520">
    <a-spin :spinning="categoryDetailLoading">
      <a-descriptions v-if="categoryDetail" bordered column="1" size="small">
        <a-descriptions-item label="分类ID">
          {{ categoryDetail.shopFoodCategoryId }}
        </a-descriptions-item>
        <a-descriptions-item label="分类名称">
          {{ categoryDetail.categoryName }}
        </a-descriptions-item>
        <a-descriptions-item label="状态">
          <a-tag :color="categoryDetail.categoryStatus === 0 ? 'green' : 'red'">
            {{ categoryDetail.categoryStatus === 0 ? '正常' : '禁用' }}
          </a-tag>
        </a-descriptions-item>
        <a-descriptions-item label="排序">
          {{ categoryDetail.sort }}
        </a-descriptions-item>
        <a-descriptions-item label="备注">
          {{ categoryDetail.remark || '-' }}
        </a-descriptions-item>
        <a-descriptions-item label="创建时间">
          {{ formatDateTime(categoryDetail.createTime) }}
        </a-descriptions-item>
        <a-descriptions-item label="修改时间">
          {{ formatDateTime(categoryDetail.modifyTime) }}
        </a-descriptions-item>
      </a-descriptions>
      <div v-else style="text-align: center; padding: 16px 0">暂无数据</div>
    </a-spin>
  </a-modal>
</template>

<script setup lang="ts">
  import { ref, reactive, onMounted } from 'vue';
  import { useRouter } from 'vue-router';
  import { SearchOutlined, ReloadOutlined, ArrowLeftOutlined } from '@ant-design/icons-vue';
  import { message } from 'ant-design-vue';
  import zhCN from 'ant-design-vue/es/locale/zh_CN';
  import dayjs, { type Dayjs } from 'dayjs';
  import { restoreSelectedShop, cacheSelectedShop } from '@/utils/selectedShop';
  import {
    fetchCategoryList,
    addFoodCategory,
    updateFoodCategory,
    deleteFoodCategory,
    forbidFoodCategory,
    fetchFoodCategoryDetail,
    insertLanguageTranslation,
    updateLanguageTranslation,
    fetchLanguageTranslationList,
  } from '@/service/food';
  import { fetchShopList } from '@/service/base';
  import type { FoodCategoryDetailModel } from '@/model/food';

  // 表格列定义
  const columns = [
    {
      title: '分类ID',
      dataIndex: 'shopFoodCategoryId',
      key: 'shopFoodCategoryId',
      width: 100,
    },
    {
      title: '分类名称',
      dataIndex: 'categoryName',
      key: 'categoryName',
      width: 150,
    },
    {
      title: '状态',
      dataIndex: 'categoryStatus',
      key: 'categoryStatus',
      width: 100,
    },
    {
      title: '店铺名称',
      dataIndex: 'shopName',
      key: 'shopName',
      width: 150,
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 180,
    },
    {
      title: '操作',
      key: 'actions',
      width: 260,
      fixed: 'right',
    },
  ];

  // 响应式数据
  const loading = ref(false);
  const shopList = ref<any[]>([]);
  const tableData = ref<any[]>([]);
  const total = ref(0);
  const dateRange = ref<[Dayjs, Dayjs] | null>(null);
  const translationLanguageOptions = [
    { label: '英语', value: 'en' },
    { label: '法语', value: 'ja' },
  ];

  // 新增 / 编辑分类弹窗
  const categoryModalVisible = ref(false);
  const categoryModalLoading = ref(false);
  const isEdit = ref(false);
  const categoryModalTitle = ref('新增分类');
  const categoryForm = reactive({
    shopFoodCategoryId: undefined as number | undefined,
    shopId: undefined as number | undefined,
    categoryName: '',
    // categoryType 后端需要，此处固定为 1
    categoryStatus: 0 as 0 | 1,
    sort: 1 as number,
    remark: '',
    translationLanguage: 'en',
    languageTranslationId: undefined as number | undefined,
    translationCategoryName: '',
  });

  // 分类详情弹窗
  const categoryDetailVisible = ref(false);
  const categoryDetailLoading = ref(false);
  const categoryDetail = ref<FoodCategoryDetailModel | null>(null);

  // 查询参数
  const queryParams = reactive({
    createtimeBegin: '',
    createtimeEnd: '',
    pageNum: 1,
    pageSize: 20,
    data: {
      shopId: undefined as number | undefined,
      categoryName: '',
      categoryStatus: undefined as number | undefined,
    },
  });

  // 格式化日期时间
  const formatDateTime = (dateStr: string | Date | undefined) => {
    if (!dateStr) return '-';
    return dayjs(dateStr).format('YYYY-MM-DD HH:mm:ss');
  };

  // 根据店铺ID获取店铺名称（用于列表展示）
  const getShopName = (shopId?: number | string) => {
    if (!shopId) return '-';
    const idNum = Number(shopId);
    const shop = shopList.value.find((s) => s.shopId === idNum);
    return shop?.shopName || '-';
  };

  const getSelectedShop = (shopId?: number) => {
    if (!shopId) return undefined;
    return shopList.value.find((shop) => Number(shop.shopId) === Number(shopId));
  };

  const submitCategoryTranslation = async (categoryId?: number) => {
    const translationCategoryName = categoryForm.translationCategoryName.trim();
    if (!translationCategoryName) return;
    if (!categoryId || !categoryForm.shopId) {
      message.warning('分类保存成功，翻译缺少分类ID或店铺信息，未保存');
      return;
    }

    const shop = getSelectedShop(categoryForm.shopId);
    if (!shop) {
      message.warning('分类保存成功，翻译缺少店铺公司信息，未保存');
      return;
    }

    const translationJson = JSON.stringify({ categoryName: translationCategoryName });
    const resp = categoryForm.languageTranslationId
      ? await updateLanguageTranslation({
          languageTranslationId: categoryForm.languageTranslationId,
          translationJson,
          remark: categoryForm.remark || '',
        })
      : await insertLanguageTranslation({
          companyId: Number(shop.companyId || 0),
          companyName: shop.companyName || '',
          language: categoryForm.translationLanguage,
          type: 3,
          entityId: categoryId,
          entityName: categoryForm.categoryName,
          translationJson,
          remark: categoryForm.remark || '',
        });
    if (resp.code === 200) {
      message.success('分类翻译保存成功');
    } else {
      message.warning(resp.msg || '分类保存成功，翻译保存失败');
    }
  };

  const parseTranslationJson = (translationJson?: string) => {
    if (!translationJson) return undefined;
    try {
      return JSON.parse(translationJson) as { categoryName?: string };
    } catch (error) {
      console.error('解析分类翻译失败:', error);
      return undefined;
    }
  };

  const loadCategoryTranslation = async (categoryId?: number, shopId?: number) => {
    categoryForm.translationCategoryName = '';
    categoryForm.languageTranslationId = undefined;
    if (!categoryId || !shopId) return;

    const shop = getSelectedShop(shopId);
    if (!shop) return;

    try {
      const resp = await fetchLanguageTranslationList({
        pageNum: 1,
        pageSize: 20,
        data: {
          companyId: Number(shop.companyId || 0),
          language: categoryForm.translationLanguage,
          type: 3,
          status: 0,
          entityId: categoryId,
        },
      });
      if (resp.code === 200) {
        const translation = resp.result.list?.[0];
        const translationData = parseTranslationJson(translation?.translationJson);
        categoryForm.languageTranslationId = translation?.languageTranslationId;
        categoryForm.translationCategoryName = translationData?.categoryName || '';
      }
    } catch (error) {
      console.error('加载分类翻译失败:', error);
    }
  };

  const handleCategoryTranslationLanguageChange = async () => {
    categoryForm.translationCategoryName = '';
    categoryForm.languageTranslationId = undefined;
    if (isEdit.value) {
      await loadCategoryTranslation(categoryForm.shopFoodCategoryId, categoryForm.shopId);
    }
  };

  // 处理日期范围变化（开始时间传 00:00:00，结束时间传 23:59:59）
  const handleDateRangeChange = (dates: [Dayjs, Dayjs] | null) => {
    if (dates && dates[0] && dates[1]) {
      // 开始时间：当天 00:00:00
      queryParams.createtimeBegin = dates[0].startOf('day').format('YYYY-MM-DD HH:mm:ss');
      // 结束时间：当天 23:59:59
      queryParams.createtimeEnd = dates[1].endOf('day').format('YYYY-MM-DD HH:mm:ss');
    } else {
      queryParams.createtimeBegin = '';
      queryParams.createtimeEnd = '';
    }
  };

  // 加载店铺列表
  const loadShopList = async () => {
    try {
      const response = await fetchShopList({
        pageNum: 1,
        pageSize: 999,
        data: { shopStatus: 0 },
      });
      if (response.code === 200) {
        shopList.value = response.result.list || [];
        queryParams.data.shopId = restoreSelectedShop(shopList.value);
      }
    } catch (error) {
      console.error('加载店铺列表失败:', error);
      message.error('加载店铺列表失败');
    }
  };

  // 加载分类列表
  const loadCategoryList = async () => {
    if (!queryParams.data.shopId) {
      tableData.value = [];
      total.value = 0;
      message.warning('请选择餐厅');
      return;
    }
    try {
      loading.value = true;

      // 构建查询参数，过滤掉空值
      const params: any = {
        pageNum: queryParams.pageNum,
        pageSize: queryParams.pageSize,
        data: {},
      };

      // 添加时间范围
      if (queryParams.createtimeBegin) {
        params.createtimeBegin = queryParams.createtimeBegin;
      }
      if (queryParams.createtimeEnd) {
        params.createtimeEnd = queryParams.createtimeEnd;
      }

      // 添加数据筛选条件（只添加有值的字段）
      if (queryParams.data.shopId !== undefined && queryParams.data.shopId !== null) {
        params.data.shopId = queryParams.data.shopId;
      }
      if (queryParams.data.categoryName) {
        params.data.categoryName = queryParams.data.categoryName;
      }
      if (
        queryParams.data.categoryStatus !== undefined &&
        queryParams.data.categoryStatus !== null
      ) {
        params.data.categoryStatus = queryParams.data.categoryStatus;
      }

      const response = await fetchCategoryList(params);

      if (response.code === 200) {
        tableData.value = response.result.list || [];
        total.value = response.result.total || 0;
      } else {
        message.error(response.msg || '查询失败');
        tableData.value = [];
        total.value = 0;
      }
    } catch (error) {
      console.error('加载分类列表失败:', error);
      message.error('加载分类列表失败，请稍后重试');
      tableData.value = [];
      total.value = 0;
    } finally {
      loading.value = false;
    }
  };

  // 查询
  const handleSearch = () => {
    queryParams.pageNum = 1;
    loadCategoryList();
  };

  // 打开新增分类弹窗
  const openAddCategoryModal = () => {
    isEdit.value = false;
    categoryModalTitle.value = '新增分类';
    categoryForm.shopFoodCategoryId = undefined;
    categoryForm.shopId = queryParams.data.shopId;
    categoryForm.categoryName = '';
    categoryForm.categoryStatus = 0;
    categoryForm.sort = 1;
    categoryForm.remark = '';
    categoryForm.translationLanguage = 'en';
    categoryForm.languageTranslationId = undefined;
    categoryForm.translationCategoryName = '';
    categoryModalVisible.value = true;
  };

  // 打开编辑分类弹窗
  const handleEditCategory = async (record: any) => {
    isEdit.value = true;
    categoryModalTitle.value = '编辑分类';
    categoryModalLoading.value = true;
    try {
      const resp = await fetchFoodCategoryDetail({
        shopFoodCategoryId: record.shopFoodCategoryId,
      });
      if (resp.code === 200 && resp.result) {
        const data = resp.result;
        categoryForm.shopFoodCategoryId = data.shopFoodCategoryId;
        // 编辑时带出所属店铺，供只读展示
        categoryForm.shopId = data.shopId as number | undefined;
        categoryForm.categoryName = data.categoryName || '';
        categoryForm.categoryStatus =
          (data.categoryStatus as 0 | 1 | undefined) !== undefined
            ? (data.categoryStatus as 0 | 1)
            : 0;
        categoryForm.sort = data.sort ?? 1;
        categoryForm.remark = data.remark || '';
        categoryForm.translationLanguage = 'en';
        categoryForm.languageTranslationId = undefined;
        categoryForm.translationCategoryName = '';
        await loadCategoryTranslation(data.shopFoodCategoryId, data.shopId);
        categoryModalVisible.value = true;
      } else {
        message.error(resp.msg || '获取分类详情失败');
      }
    } catch (error) {
      console.error('获取分类详情异常:', error);
      message.error('获取分类详情异常，请稍后重试');
    } finally {
      categoryModalLoading.value = false;
    }
  };

  // 保存分类（新增 / 编辑）
  const handleCategoryModalOk = async () => {
    // 新增时必须选择店铺
    if (!isEdit.value && !categoryForm.shopId) {
      message.warning('请选择店铺');
      return;
    }
    if (!categoryForm.categoryName) {
      message.warning('请输入分类名称');
      return;
    }
    if (categoryForm.sort < 0) {
      message.warning('排序值不能为负数');
      return;
    }

    categoryModalLoading.value = true;
    try {
      if (isEdit.value) {
        // 编辑
        const payload = {
          shopFoodCategoryId: categoryForm.shopFoodCategoryId,
          categoryName: categoryForm.categoryName,
          categoryType: 1,
          sort: categoryForm.sort,
          remark: categoryForm.remark,
        };
        const resp = await updateFoodCategory(payload);
        if (resp.code === 200) {
          message.success('编辑分类成功');
          await submitCategoryTranslation(categoryForm.shopFoodCategoryId);
          categoryModalVisible.value = false;
          await loadCategoryList();
        } else {
          message.error(resp.msg || '编辑分类失败');
        }
      } else {
        // 新增
        const payload = {
          shopId: categoryForm.shopId,
          categoryName: categoryForm.categoryName,
          categoryType: 1,
          categoryStatus: categoryForm.categoryStatus ?? 0,
          sort: categoryForm.sort,
          remark: categoryForm.remark,
        };
        const resp = await addFoodCategory(payload);
        if (resp.code === 200) {
          message.success('新增分类成功');
          await submitCategoryTranslation(Number(resp.result || 0));
          categoryModalVisible.value = false;
          await loadCategoryList();
        } else {
          message.error(resp.msg || '新增分类失败');
        }
      }
    } catch (error) {
      console.error('保存分类异常:', error);
      message.error('保存分类异常，请稍后重试');
    } finally {
      categoryModalLoading.value = false;
    }
  };

  // 取消分类弹窗
  const handleCategoryModalCancel = () => {
    categoryModalVisible.value = false;
    categoryForm.translationLanguage = 'en';
    categoryForm.languageTranslationId = undefined;
    categoryForm.translationCategoryName = '';
  };

  // 禁用 / 启用 分类
  const handleToggleCategoryStatus = async (record: any) => {
    const targetStatus = record.categoryStatus === 0 ? 1 : 0;
    try {
      const resp = await forbidFoodCategory({
        shopFoodCategoryId: record.shopFoodCategoryId,
        categoryStatus: targetStatus,
      });
      if (resp.code === 200) {
        message.success(targetStatus === 1 ? '分类已禁用' : '分类已启用');
        await loadCategoryList();
      } else {
        message.error(resp.msg || '更新分类状态失败');
      }
    } catch (error) {
      console.error('更新分类状态异常:', error);
      message.error('更新分类状态异常，请稍后重试');
    }
  };

  // 删除分类
  const handleDeleteCategory = async (record: any) => {
    try {
      const resp = await deleteFoodCategory({
        shopFoodCategoryId: record.shopFoodCategoryId,
      });
      if (resp.code === 200) {
        message.success('删除分类成功');
        await loadCategoryList();
      } else {
        message.error(resp.msg || '删除分类失败');
      }
    } catch (error) {
      console.error('删除分类异常:', error);
      message.error('删除分类异常，请稍后重试');
    }
  };

  // 查看分类详情
  const handleViewCategoryDetail = async (record: any) => {
    categoryDetailVisible.value = true;
    categoryDetailLoading.value = true;
    categoryDetail.value = null;
    try {
      const resp = await fetchFoodCategoryDetail({
        shopFoodCategoryId: record.shopFoodCategoryId,
      });
      if (resp.code === 200 && resp.result) {
        categoryDetail.value = resp.result;
      } else {
        message.error(resp.msg || '获取分类详情失败');
      }
    } catch (error) {
      console.error('获取分类详情异常:', error);
      message.error('获取分类详情异常，请稍后重试');
    } finally {
      categoryDetailLoading.value = false;
    }
  };

  // 重置
  const handleReset = () => {
    queryParams.pageNum = 1;
    queryParams.pageSize = 20;
    queryParams.createtimeBegin = '';
    queryParams.createtimeEnd = '';
    queryParams.data.categoryName = '';
    queryParams.data.categoryStatus = undefined;
    dateRange.value = null;
    loadCategoryList();
  };

  // 分页变化
  const handlePageChange = (page: number) => {
    queryParams.pageNum = page;
    loadCategoryList();
  };

  // 每页条数变化
  const handlePageSizeChange = (current: number, size: number) => {
    queryParams.pageNum = 1;
    queryParams.pageSize = size;
    loadCategoryList();
  };

  // 路由
  const router = useRouter();

  // 返回上一页
  const goBack = () => {
    router.back();
  };

  // 初始化
  onMounted(async () => {
    await loadShopList();
    await loadCategoryList();
  });
</script>

<style scoped lang="less">
  .food-category-manage {
    padding: 24px;
    background: #f0f2f5;
    min-height: 100vh;

    .search-form {
      margin-bottom: 16px;
      padding: 16px;
      background: #fff;
      border-radius: 4px;

      :deep(.ant-form-item) {
        margin-bottom: 16px;
      }
    }

    .pagination-container {
      margin-top: 16px;
      text-align: right;
    }

    :deep(.ant-card) {
      .ant-card-head {
        border-bottom: 1px solid #f0f0f0;
      }

      .ant-card-body {
        padding: 24px;
      }
    }

    :deep(.ant-table) {
      .ant-table-thead > tr > th {
        background: #fafafa;
        font-weight: 600;
      }
    }
  }
</style>
